import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  BarController,
  LineController,
  DoughnutController,
  ScatterController,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartData,
  type ChartDataset,
  type ChartOptions,
} from 'chart.js';
import type { ChartArtifactPayload } from '@cotera/api';
import { getChartPaletteColor, readChartThemeColor } from './chart-palette';
import {
  chartLabelToPaletteIndex,
  buildChartColorResolver,
  collectChartColorLabels,
} from './chart-label-colors';

export {
  CHART_SERIES_COLORS,
  CHART_PALETTE_SIZE,
  getChartPaletteColor,
  readChartThemeColor,
} from './chart-palette';
export {
  normalizeChartColorLabel,
  chartLabelToPaletteIndex,
  collectChartColorLabels,
  chartPayloadAccentLabel,
  type ChartColorResolver,
} from './chart-label-colors';

let registered = false;

export function ensureChartJsRegistered(): void {
  if (registered) {
    return;
  }
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    BarController,
    LineController,
    DoughnutController,
    ScatterController,
    Title,
    Tooltip,
    Legend,
    Filler
  );
  registered = true;
}

/** Register before any chart mounts — useEffect is too late (first paint runs first). */
ensureChartJsRegistered();

export function getChartColorForLabel(label: string): string {
  return getChartPaletteColor(chartLabelToPaletteIndex(label));
}

function getChartAxisColor(): string {
  return readChartThemeColor('--muted-foreground', 'oklch(0.52 0.04 80)');
}

function getChartGridColor(): string {
  return readChartThemeColor('--border', 'oklch(0.93 0.01 91)');
}

function softChartScales(options: {
  stacked?: boolean;
  xType?: 'category' | 'linear';
  xTickCallback?: (tickValue: string | number) => string;
}): ChartOptions['scales'] {
  const axisColor = getChartAxisColor();
  const gridColor = getChartGridColor();
  return {
    x: {
      type: options.xType ?? 'category',
      stacked: options.stacked === true,
      grid: { display: false },
      border: { color: gridColor },
      ticks: {
        color: axisColor,
        font: { size: 11 },
        ...(options.xTickCallback !== undefined
          ? {
              callback(tickValue: string | number) {
                return options.xTickCallback!(tickValue);
              },
            }
          : {}),
      },
    },
    y: {
      type: 'linear',
      stacked: options.stacked === true,
      beginAtZero: true,
      grid: { color: gridColor, tickLength: 0 },
      border: { display: false },
      ticks: {
        color: axisColor,
        font: { size: 11 },
      },
    },
  };
}

function softChartPlugins(showLegend: boolean): ChartOptions['plugins'] {
  const axisColor = getChartAxisColor();
  return {
    legend: {
      display: showLegend,
      position: 'bottom',
      labels: {
        color: axisColor,
        boxWidth: 10,
        boxHeight: 10,
        padding: 14,
        useBorderRadius: true,
        borderRadius: CHART_LEGEND_BORDER_RADIUS,
        filter(legendItem) {
          return legendItem.text !== CHART_BREAKPOINT_DATASET_LABEL;
        },
      },
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(context) {
          if (context.dataset.label !== CHART_BREAKPOINT_DATASET_LABEL) {
            return undefined;
          }
          if (!isBreakpointPoint(context.raw) || !context.raw.isMarker) {
            return undefined;
          }
          const description = context.raw.breakpointDescription ?? '';
          return description !== ''
            ? `${context.raw.breakpointLabel}: ${description}`
            : context.raw.breakpointLabel;
        },
      },
    },
  };
}

function withAlpha(color: string, alpha: number): string {
  if (color.includes('oklch(') && !color.includes('/')) {
    return color.replace(')', ` / ${alpha})`);
  }
  return color;
}

const CHART_BAR_BORDER_RADIUS = 4;
const CHART_LEGEND_BORDER_RADIUS = 3;
const CHART_BREAKPOINT_DATASET_LABEL = '__cotera_chart_breakpoints__';

function breakpointIndex(
  breakpoint: NonNullable<ChartArtifactPayload['breakpoints']>[number],
  labels: string[]
): number | null {
  if (breakpoint.x_label !== null && breakpoint.x_label !== '') {
    const index = labels.indexOf(breakpoint.x_label);
    return index === -1 ? null : index;
  }

  if (
    breakpoint.label_index !== null &&
    Number.isInteger(breakpoint.label_index) &&
    breakpoint.label_index >= 0 &&
    breakpoint.label_index < labels.length
  ) {
    return breakpoint.label_index;
  }

  return null;
}

type ChartBreakpointPoint = {
  x: string;
  y: number;
  base: number;
  breakpointLabel: string;
  breakpointDescription: string | null;
  isMarker: boolean;
};

type LineChartDataPoint = number | ChartBreakpointPoint | null;

function breakpointYRange(
  datasets: ChartDataset<'line', LineChartDataPoint[]>[]
): {
  min: number;
  max: number;
} | null {
  const values = datasets
    .flatMap((dataset) => dataset.data)
    .filter((value): value is number => typeof value === 'number');

  if (values.length === 0) {
    return null;
  }

  return {
    min: Math.min(0, ...values),
    max: Math.max(0, ...values),
  };
}

function breakpointColor(color: string | null): string {
  return color !== null && color !== ''
    ? color
    : readChartThemeColor('--foreground', 'oklch(0.22 0.02 80)');
}

function isBreakpointPoint(value: unknown): value is ChartBreakpointPoint {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const point = value as Record<string, unknown>;
  return (
    typeof point['x'] === 'string' &&
    typeof point['y'] === 'number' &&
    typeof point['base'] === 'number' &&
    typeof point['breakpointLabel'] === 'string'
  );
}

function buildBreakpointDatasets(
  payload: ChartArtifactPayload,
  datasets: ChartDataset<'line', LineChartDataPoint[]>[]
): ChartDataset<'line', LineChartDataPoint[]>[] {
  const breakpoints = payload.breakpoints ?? [];
  if (breakpoints.length === 0) {
    return [];
  }

  const yRange = breakpointYRange(datasets);
  if (yRange === null) {
    return [];
  }

  const markerBorderColor = readChartThemeColor(
    '--background',
    'oklch(0.98 0.004 91)'
  );
  const breakpointDatasets: ChartDataset<'line', LineChartDataPoint[]>[] = [];

  for (const breakpoint of breakpoints) {
    const index = breakpointIndex(breakpoint, payload.data.labels);
    if (index === null) {
      continue;
    }

    const color = breakpointColor(breakpoint.color);
    breakpointDatasets.push({
      label: CHART_BREAKPOINT_DATASET_LABEL,
      data: [
        {
          x: payload.data.labels[index] ?? '',
          y: yRange.min,
          base: yRange.min,
          breakpointLabel: breakpoint.label,
          breakpointDescription: breakpoint.description,
          isMarker: false,
        },
        {
          x: payload.data.labels[index] ?? '',
          y: yRange.max,
          base: yRange.min,
          breakpointLabel: breakpoint.label,
          breakpointDescription: breakpoint.description,
          isMarker: true,
        },
      ],
      borderColor: color,
      backgroundColor: 'transparent',
      pointBackgroundColor: ['transparent', color],
      pointBorderColor: markerBorderColor,
      pointBorderWidth: 1.5,
      pointRadius: [0, 4],
      pointHoverRadius: [0, 4],
      pointStyle: 'triangle',
      showLine: true,
      borderWidth: 1,
      borderDash: [4, 4],
      order: -1,
      parsing: {
        xAxisKey: 'x',
        yAxisKey: 'y',
      },
      spanGaps: false,
      tension: 0,
    });
  }

  return breakpointDatasets;
}

export type ChartJsViewModel =
  | {
      chartKind: 'bar';
      data: ChartData<'bar'>;
      options: ChartOptions;
    }
  | {
      chartKind: 'line';
      data: ChartData<'line', LineChartDataPoint[]>;
      options: ChartOptions;
    }
  | {
      chartKind: 'doughnut';
      data: ChartData<'doughnut'>;
      options: ChartOptions;
    }
  | {
      chartKind: 'scatter';
      data: ChartData<'scatter'>;
      options: ChartOptions;
    };

export function toChartJsViewModel(
  payload: ChartArtifactPayload,
  colorForLabel?: (label: string) => string
): ChartJsViewModel {
  // Default resolver de-collides within the chart so distinct series/slices get
  // distinct colors (see buildChartColorResolver). A caller-supplied resolver
  // (e.g. status colors in workflow stats) is used verbatim.
  const resolveColor =
    colorForLabel ?? buildChartColorResolver(collectChartColorLabels(payload));
  const showLegend = payload.options?.show_legend !== false;
  const stacked = payload.chart_type === 'stacked_bar';
  const isArea = payload.chart_type === 'area';
  const isPie = payload.chart_type === 'pie';
  const isScatter = payload.chart_type === 'scatter';

  const baseOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: softChartPlugins(showLegend && !isPie),
  };

  if (isPie) {
    const dataset = payload.data.datasets[0];
    const values = dataset?.values ?? [];
    const colors = payload.data.labels.map((label: string) =>
      resolveColor(label)
    );
    return {
      chartKind: 'doughnut',
      data: {
        labels: payload.data.labels,
        datasets: [
          {
            label: dataset?.label ?? payload.title,
            data: values,
            backgroundColor: colors,
            borderWidth: 0,
            hoverBorderWidth: 1,
            borderColor: readChartThemeColor(
              '--background',
              'oklch(0.98 0.004 91)'
            ),
          },
        ],
      },
      options: {
        ...baseOptions,
        plugins: {
          ...softChartPlugins(showLegend),
          title: {
            display: payload.title !== '',
            text: payload.title,
            color: getChartAxisColor(),
            font: { size: 14 },
          },
        },
      },
    };
  }

  if (isScatter) {
    const datasets = payload.data.datasets.map(
      (ds: ChartArtifactPayload['data']['datasets'][number], i: number) => {
        const seriesLabel = ds.label ?? `Series ${i + 1}`;
        const color = resolveColor(seriesLabel);
        const points = payload.data.labels.map(
          (_label: string, idx: number) => ({
            x: idx,
            y: ds.values[idx] ?? 0,
          })
        );
        return {
          label: seriesLabel,
          data: points,
          backgroundColor: color,
          borderColor: color,
        };
      }
    );
    return {
      chartKind: 'scatter',
      data: { datasets },
      options: {
        ...baseOptions,
        scales: softChartScales({
          xType: 'linear',
          xTickCallback: (tickValue) => {
            const index =
              typeof tickValue === 'number'
                ? tickValue
                : Number.parseInt(String(tickValue), 10);
            return payload.data.labels[index] ?? '';
          },
        }),
      },
    };
  }

  const chartKind = payload.chart_type === 'line' || isArea ? 'line' : 'bar';
  const titleOptions = {
    display: payload.title !== '',
    text: payload.title,
    color: getChartAxisColor(),
    font: { size: 14 },
  };

  if (chartKind === 'line') {
    const datasets: ChartDataset<'line', LineChartDataPoint[]>[] =
      payload.data.datasets.map(
        (ds: ChartArtifactPayload['data']['datasets'][number], i: number) => {
          const seriesLabel = ds.label ?? `Series ${i + 1}`;
          const color = resolveColor(seriesLabel);
          return {
            label: seriesLabel,
            data: ds.values,
            backgroundColor: isArea
              ? withAlpha(color, 0.22)
              : withAlpha(color, 0.88),
            borderColor: color,
            borderWidth: 1.5,
            pointBackgroundColor: color,
            pointBorderColor: readChartThemeColor(
              '--background',
              'oklch(0.98 0.004 91)'
            ),
            pointBorderWidth: 1,
            pointRadius: 2,
            fill: isArea,
            tension: 0.35,
          };
        }
      );
    const breakpointDatasets = buildBreakpointDatasets(payload, datasets);

    return {
      chartKind,
      data: {
        labels: payload.data.labels,
        datasets:
          breakpointDatasets.length === 0
            ? datasets
            : [...datasets, ...breakpointDatasets],
      },
      options: {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          title: titleOptions,
        },
        scales: softChartScales({ stacked }),
      },
    };
  }

  const datasets: ChartDataset<'bar'>[] = payload.data.datasets.map(
    (ds: ChartArtifactPayload['data']['datasets'][number], i: number) => {
      const seriesLabel = ds.label ?? `Series ${i + 1}`;
      const color = resolveColor(seriesLabel);
      return {
        label: seriesLabel,
        data: ds.values,
        backgroundColor: withAlpha(color, 0.88),
        borderColor: color,
        borderWidth: 0,
        borderRadius: CHART_BAR_BORDER_RADIUS,
        pointBackgroundColor: color,
        pointBorderColor: readChartThemeColor(
          '--background',
          'oklch(0.98 0.004 91)'
        ),
        pointBorderWidth: 1,
        pointRadius: 0,
        fill: false,
        tension: 0.35,
      };
    }
  );

  return {
    chartKind,
    data: {
      labels: payload.data.labels,
      datasets,
    },
    options: {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        title: titleOptions,
      },
      scales: softChartScales({ stacked }),
    },
  };
}
